import { FC, Fragment } from 'react';
import { Button, Col, Collapse, Divider, Form, Row, Select, Table } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import NAPWholesale from './NAPPage.Wholesale';
import NAPApplicationBG from './NAPPage.ApplicationBG';
import NAPCompanyGroup from './NAPPage.CompanyGroup';
import NAPFullfillment from './NAPPage.Fullfillment';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getNAPDetail,
	NAPApprovalPayload,
	submitNAPApproval,
} from '@/libs/fleet-api/services/nap';
import MainInformation from '@/components/MainInformation';
import { LoadingOutlined } from '@ant-design/icons';
import NAPRiskMitigation from './NAPPage.RiskMitigation';
import NAPRecommendation from './NAPPage.Recommendation';
import FleetTitle from '@/libs/antd/components/Title';
import dayjs from 'dayjs';
import { useUserData } from '@/libs/fleet-api/hooks';
import NAPAnalysisForm from './NAPPage.Analysis';
import { useModals } from '@/components/modals/ModalProvider';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import TextArea from 'antd/es/input/TextArea';
import { useForm } from '@/libs/antd/form';
import NAPSummaryConsent from './jf/NAPPage.SummaryConsent';
import NAPEDDPEPAnalysis from './jf/NAPPage.EDDPEP';
import NAPSpecialCondition from './jf/NAPPage.SpecialCondition';
import { useApp } from '@/App';

const NAPPage: FC<{ prospectId: string; onBack?: () => void }> = ({
	prospectId,
	onBack,
}) => {
	const { navigate } = useApp();
	const { openPipelineModal, openLKPDFModal } = useModals();
	const { groupName } = useUserData() ?? {};

	const { data, isFetching, refetch } = useQuery({
		queryKey: ['getNAPDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPDetail(id);
		},
	});

	const approvalForm = useForm<NAPApprovalPayload>();

	const submitNAPApprovalMutation = useMutation({
		mutationFn: submitNAPApproval,
	});

	const {
		napId,
		plafonType,
		napInformation,
		wholesale,
		internalCompliance,
		financialAnalysis,
		backgroundInformation,
		companyAndGroupInformation,
		specialConditionSubmission,
		riskAndMitigation,
		recommendation,
		summaryConsent,
		eddPepAnalysis,
		approvalDetail,
	} = data?.data ?? {};

	const {
		mainInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		pipelineInformation,
	} = napInformation ?? {};

	const {
		actionOptions,
		componentOptions,
		napApprovalHistory,
		hasApprovalPermission,
	} = approvalDetail ?? {};

	const showDownloadNAPNJF =
		wholesale?.status === 'VERIFIED' &&
		backgroundInformation?.status === 'VERIFIED' &&
		companyAndGroupInformation?.status === 'VERIFIED' &&
		internalCompliance?.status === 'VERIFIED' &&
		financialAnalysis?.status === 'VERIFIED' &&
		riskAndMitigation?.status === 'VERIFIED' &&
		recommendation?.status === 'VERIFIED';

	const showDownloadNAPJF =
		wholesale?.status === 'VERIFIED' &&
		summaryConsent?.status === 'VERIFIED' &&
		eddPepAnalysis?.status === 'VERIFIED' &&
		riskAndMitigation?.status === 'VERIFIED' &&
		recommendation?.status === 'VERIFIED';

	const renderCheck = (isChecked: boolean) => {
		return (
			<span
				style={{
					fontSize: 24,
					lineHeight: 1,
					color: isChecked ? '#39A905' : '#9A9A9A',
				}}
			>
				<CheckCircleFilled />
			</span>
		);
	};

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<MainInformation
				onBack={onBack}
				mainInformation={mainInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				showName={false}
				extraItems={[
					[
						['Ultimate Owner', pipelineInformation?.ultimateOwner],
						[
							'Pipeline ID',
							pipelineInformation?.pipelineId ? (
								<a
									onClick={(e) => {
										e.preventDefault();
										openPipelineModal(pipelineInformation.pipelineId);
									}}
								>
									{pipelineInformation.pipelineNo ??
										pipelineInformation.pipelineId}
								</a>
							) : null,
						],
					],
					[
						[
							'Hasil Laporan Kunjungan Kantor',
							<a
								onClick={(e) => {
									e.preventDefault();
									openLKPDFModal({
										type: 'lk-office',
										prospectId,
									});
								}}
							>
								View Dokumen
							</a>,
						],
						[
							'Hasil Laporan Kunjungan Site',
							<a
								onClick={(e) => {
									e.preventDefault();
									openLKPDFModal({
										type: 'lk-site',
										prospectId,
									});
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			{plafonType === 'NON_JOIN_FINANCING' && (
				<Collapse
					defaultActiveKey={[]}
					items={[
						{
							label: 'Nota Analisa Pembiayaan (NAP) - Non JF Wholesale',
							extra: renderCheck(
								(groupName === 'FRM' && wholesale?.status === 'SUBMITTED') ||
									wholesale?.status === 'VERIFIED',
							),
							collapsible:
								wholesale?.action === 'DISABLED' ? 'disabled' : undefined,
							children: (
								<NAPWholesale
									plafonType="NON_JOIN_FINANCING"
									napState={wholesale}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitded={refetch}
								/>
							),
						},
						{
							label: 'Latar Belakang Permohonan',
							extra: renderCheck(
								(groupName === 'FRM' &&
									backgroundInformation?.status === 'SUBMITTED') ||
									backgroundInformation?.status === 'VERIFIED',
							),
							collapsible:
								backgroundInformation?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							children: (
								<NAPApplicationBG
									napState={backgroundInformation}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Informasi Perusahaan dan Group Perusahaan',
							collapsible:
								companyAndGroupInformation?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							extra: renderCheck(
								(groupName === 'FRM' &&
									companyAndGroupInformation?.status === 'SUBMITTED') ||
									companyAndGroupInformation?.status === 'VERIFIED',
							),
							children: (
								<NAPCompanyGroup
									napState={companyAndGroupInformation}
									customerId={mainInformation!.customerId}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Pemenuhan Ketentuan Internal MTF',
							collapsible:
								internalCompliance?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							extra: renderCheck(
								(groupName === 'FRM' &&
									internalCompliance?.status === 'SUBMITTED') ||
									internalCompliance?.status === 'VERIFIED',
							),
							children: (
								<NAPFullfillment
									napState={internalCompliance}
									customerId={mainInformation!.customerId}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Analisa Pembiayaan',
							collapsible:
								financialAnalysis?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							extra: renderCheck(
								(groupName === 'FRM' &&
									financialAnalysis?.status === 'SUBMITTED') ||
									financialAnalysis?.status === 'VERIFIED',
							),
							children: (
								<NAPAnalysisForm
									// napState={financialAnalysis}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Risiko dan Mitigasi',
							collapsible:
								riskAndMitigation?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							extra: renderCheck(riskAndMitigation?.status === 'VERIFIED'),
							children: (
								<NAPRiskMitigation
									plafonType="NON_JOIN_FINANCING"
									napState={riskAndMitigation}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Rekomendasi',
							collapsible:
								recommendation?.action === 'DISABLED' ? 'disabled' : undefined,
							extra: renderCheck(recommendation?.status === 'VERIFIED'),
							children: (
								<NAPRecommendation
									plafonType="NON_JOIN_FINANCING"
									napState={recommendation}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitted={refetch}
								/>
							),
						},
					]}
				/>
			)}

			{plafonType === 'JOIN_FINANCING' && (
				<Collapse
					defaultActiveKey={[]}
					items={[
						{
							label: 'Nota Analisa Pembiayaan (NAP) - JF Wholesale',
							extra: renderCheck(
								(groupName === 'FRM' && wholesale?.status === 'SUBMITTED') ||
									wholesale?.status === 'VERIFIED',
							),
							collapsible:
								wholesale?.action === 'DISABLED' ? 'disabled' : undefined,
							children: (
								<NAPWholesale
									plafonType="JOIN_FINANCING"
									napState={wholesale}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitded={refetch}
								/>
							),
						},
						{
							label: 'Pengajuan Kondisi Khusus',
							extra: renderCheck(
								(groupName === 'FRM' &&
									specialConditionSubmission?.status === 'SUBMITTED') ||
									specialConditionSubmission?.status === 'VERIFIED',
							),
							collapsible:
								specialConditionSubmission?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							children: (
								<NAPSpecialCondition
									napState={specialConditionSubmission}
									napId={data?.data.napId}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Summary Consent',
							extra: renderCheck(
								(groupName === 'FRM' &&
									summaryConsent?.status === 'SUBMITTED') ||
									summaryConsent?.status === 'VERIFIED',
							),
							collapsible:
								summaryConsent?.action === 'DISABLED' ? 'disabled' : undefined,
							children: (
								<NAPSummaryConsent
									napState={summaryConsent}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label:
								'Analisa EDD (Enhanced Due Diligence) High Risk & Politically Exposed Person (PEP)',
							extra: renderCheck(
								(groupName === 'FRM' &&
									eddPepAnalysis?.status === 'SUBMITTED') ||
									eddPepAnalysis?.status === 'VERIFIED',
							),
							collapsible:
								eddPepAnalysis?.action === 'DISABLED' ? 'disabled' : undefined,
							children: (
								<NAPEDDPEPAnalysis
									napId={data?.data.napId}
									napState={eddPepAnalysis}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Aspek Resiko',
							extra: renderCheck(
								(groupName === 'FRM' &&
									riskAndMitigation?.status === 'SUBMITTED') ||
									riskAndMitigation?.status === 'VERIFIED',
							),
							collapsible:
								riskAndMitigation?.action === 'DISABLED'
									? 'disabled'
									: undefined,
							children: (
								<NAPRiskMitigation
									plafonType="JOIN_FINANCING"
									napState={riskAndMitigation}
									prospectId={prospectId}
									onSubmitted={refetch}
								/>
							),
						},
						{
							label: 'Rekomendasi',
							extra: renderCheck(
								(groupName === 'FRM' &&
									recommendation?.status === 'SUBMITTED') ||
									recommendation?.status === 'VERIFIED',
							),
							collapsible:
								recommendation?.action === 'DISABLED' ? 'disabled' : undefined,
							children: (
								<NAPRecommendation
									plafonType="JOIN_FINANCING"
									napState={recommendation}
									prospectId={prospectId}
									customerId={mainInformation!.customerId}
									onSubmitted={refetch}
								/>
							),
						},
					]}
				/>
			)}

			{showDownloadNAPNJF && (
				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginBlock: 20,
					}}
					onClick={() => navigate('NAPDownloadPage', { prospectId })}
				>
					DOWNLOAD NAP
				</Button>
			)}

			{showDownloadNAPJF && (
				<Button
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginBlock: 20,
					}}
					onClick={() => navigate('NAPJFDownloadPage', { prospectId })}
				>
					DOWNLOAD NAP
				</Button>
			)}

			{napApprovalHistory?.length ? (
				<Fragment>
					<FleetTitle>Approval History</FleetTitle>

					<Table
						style={{ marginBlock: 20 }}
						bordered
						pagination={false}
						dataSource={napApprovalHistory.map((data, key) => {
							return { ...data, key };
						})}
						columns={[
							{
								title: 'No',
								align: 'center',
								render: (_, __, index) => index + 1,
							},
							{
								title: 'Name',
								render: (_, record) => record.name,
							},
							{
								title: 'Date',
								render: (_, record) =>
									record.date && dayjs(record.date).format('DD/MM/YYYY'),
							},
							{
								title: 'Process',
								render: (_, record) => record.process,
							},
							{
								title: 'Status',
								render: (_, record) => record.status,
							},
							{
								title: 'Approval Notes',
								render: (_, record) => record.notes,
							},
						]}
					/>
				</Fragment>
			) : null}

			{hasApprovalPermission && (
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					labelCol={{ span: 10 }}
					wrapperCol={{ span: 14 }}
					form={approvalForm.form}
					onFinish={async (values) => {
						await submitNAPApprovalMutation.mutateAsync(values);
						approvalForm.form.resetFields();
						navigate('NAPListPage');
					}}
				>
					<Form.Item
						name={approvalForm.fieldName('napId')}
						initialValue={napId}
						noStyle
					/>
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								label="Approval Status"
								rules={[rule.required()]}
								name={approvalForm.fieldName('action')}
							>
								<Select
									options={actionOptions?.map((data) => {
										return {
											value: data.id,
											label: data.value,
										};
									})}
								/>
							</Form.Item>
						</Col>
						{approvalForm.value?.action === 'RETURN' && (
							<Col span={12}>
								<Form.Item
									label="Return to Step"
									name={approvalForm.fieldName('returnTos')}
									rules={[rule.required()]}
									labelCol={{
										span: 10,
										style: { alignSelf: 'flex-start' },
									}}
								>
									<Select
										mode="multiple"
										options={componentOptions?.map((data) => {
											return {
												value: data.id,
												label: data.value,
											};
										})}
									/>
								</Form.Item>
							</Col>
						)}
					</Row>

					<Divider />

					<Form.Item
						name={approvalForm.fieldName('notes')}
						label="Approval Notes"
						labelCol={{
							span: 5,
							style: {
								alignSelf: 'flex-start',
							},
						}}
						wrapperCol={{
							span: 19,
							style: { marginInline: -10 },
						}}
						rules={[rule.required()]}
					>
						<TextArea rows={4} />
					</Form.Item>

					<Button
						type="primary"
						htmlType="submit"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
							marginTop: 40,
						}}
						loading={submitNAPApprovalMutation.isPending}
						disabled={submitNAPApprovalMutation.isPending}
					>
						SAVE
					</Button>
				</Form>
			)}
		</Fragment>
	);
};

export default NAPPage;
